import { useState } from 'react';
import { Link } from 'react-router';
import {
  Wallet,
  Plus,
  RefreshCw,
  Trash2,
  Settings,
  Bot,
  DollarSign,
  Activity,
  Target,
  Lock,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMt5Accounts, useDeleteMt5Account, useSyncMt5Balance, useUpdateMt5Account } from '@/hooks/use-accounts';
import { usePlanLimits } from '@/hooks/use-plan-limits';
import { useBots } from '@/hooks/use-bots';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { formatCurrency, formatPnl, pnlColor, timeAgo } from '@/lib/format';
import { useCurrency } from '@/lib/currency';
import type { Mt5Account, BotInstance } from '@/types/api';

function ConnectionDot({ status }: { status: Mt5Account['worker_status'] }) {
  if (!status) return null;
  const colors: Record<string, string> = {
    RUNNING: 'bg-green',
    UPDATING: 'bg-yellow-400',
    UNHEALTHY: 'bg-orange',
    STOPPED: 'bg-ink-4',
    DISCONNECTED: 'bg-red',
  };
  const labels: Record<string, string> = {
    RUNNING: 'Engine connected',
    UPDATING: 'Engine updating',
    UNHEALTHY: 'Engine unhealthy',
    STOPPED: 'Engine stopped',
    DISCONNECTED: 'Engine disconnected',
  };
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${colors[status] ?? 'bg-ink-4'}`}
      title={labels[status] ?? status}
    />
  );
}

export default function AccountListPage() {
  const { data: accounts, isLoading: accountsLoading, error } = useMt5Accounts({ refetchInterval: 10_000 });
  const { data: bots, isLoading: botsLoading } = useBots({ refetchInterval: 10_000 });
  const limits = usePlanLimits();
  const { homeCurrency, convert } = useCurrency();

  const isLoading = accountsLoading || botsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-sm rounded-lg p-6 border-red/20 text-center">
        <p className="text-red text-sm font-semibold mb-2">Failed to load accounts</p>
        <p className="text-xs text-ink-3">Check that the API server is running.</p>
      </div>
    );
  }

  const all = accounts ?? [];
  const allBots = bots ?? [];
  const atAccountLimit = all.length >= limits.maxMt5Accounts;

  // Aggregate stats — only LIVE bots (exclude paper and archived)
  const liveBots = allBots.filter(b => b.botMode === 'LIVE' && !b.archived_at);
  const totalBalance = all.reduce((sum, a) => sum + convert(a.synced_balance ?? 0, a.currency || 'USD'), 0);
  const totalAllocated = liveBots.reduce((sum, b) => sum + convert(b.assigned_capital, b.mt5Account?.currency || 'USD'), 0);
  const totalBots = liveBots.length;
  const activeBots = liveBots.filter(b => b.is_active).length;
  const totalPnl = liveBots.reduce((sum, b) => sum + convert(b.combined_pnl ?? b.total_pnl, b.mt5Account?.currency || 'USD'), 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold tracking-tight">Accounts</h1>
          <p className="text-xs text-ink-3 mt-0.5">
            {all.length}/{limits.maxMt5Accounts} MT5 account{limits.maxMt5Accounts !== 1 ? 's' : ''} linked
          </p>
        </div>
        {atAccountLimit ? (
          <Button size="sm" disabled>
            <Lock size={14} />
            Limit Reached
          </Button>
        ) : (
          <Link to="/accounts/new">
            <Button size="sm">
              <Plus size={14} />
              Add Account
            </Button>
          </Link>
        )}
      </div>

      {/* Aggregate Stats */}
      {all.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={<DollarSign size={14} />} label="Total Balance" value={formatCurrency(totalBalance, homeCurrency)} />
          <StatCard icon={<Target size={14} />} label="Allocated" value={formatCurrency(totalAllocated, homeCurrency)} />
          <StatCard icon={<Bot size={14} />} label="Bots" value={`${activeBots} active / ${totalBots}`} />
          <StatCard icon={<Activity size={14} />} label="Total P&L" value={formatPnl(totalPnl, homeCurrency)} valueClass={pnlColor(totalPnl)} />
        </div>
      )}

      {/* Account List */}
      {all.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-4">
          {all.map((account) => (
            <AccountCard
              key={account.id}
              account={account}
              bots={allBots.filter(b => b.mt5Account?.id === account.id && b.botMode === 'LIVE' && !b.archived_at)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────

function StatCard({ icon, label, value, valueClass }: {
  icon: React.ReactNode;
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="glass-sm rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-ink-4">{icon}</span>
        <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider">{label}</p>
      </div>
      <p className={`text-sm font-extrabold font-mono ${valueClass ?? ''}`}>{value}</p>
    </div>
  );
}

function AccountCard({ account, bots }: { account: Mt5Account; bots: BotInstance[] }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const deleteMutation = useDeleteMt5Account();
  const syncMutation = useSyncMt5Balance();

  const handleSync = () => {
    const toastId = toast.loading('Syncing balance from broker...');
    syncMutation.mutate(account.id, {
      onSuccess: (data: any) => {
        if (data?.realBalance != null && data.realBalance > 0) {
          toast.success(`Balance synced: ${formatCurrency(data.realBalance)}`, { id: toastId });
        } else {
          toast.warning('Sync completed but balance not available yet. The broker may still be connecting.', { id: toastId });
        }
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || 'Failed to sync balance', { id: toastId });
      },
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate(account.id, {
      onSuccess: () => setShowDeleteModal(false),
    });
  };

  const totalBalance = account.synced_balance ?? 0;
  const totalAllocated = bots.reduce((sum, b) => sum + b.assigned_capital, 0);
  const unallocated = totalBalance - totalAllocated;
  const runningCount = bots.filter(b => b.is_active).length;

  return (
    <>
      <div className="glass-sm rounded-lg overflow-hidden">
        {/* Header */}
        <div className="p-4 pb-0 space-y-1.5">
          {/* Row 1 — Name + action buttons */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <ConnectionDot status={account.worker_status} />
              <span className="text-sm font-bold truncate">
                {account.name || `Account #${account.login_id}`}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={handleSync}
                disabled={syncMutation.isPending}
                className="w-7 h-7 rounded-md flex items-center justify-center text-ink-4 hover:text-orange hover:bg-orange/10 transition-colors cursor-pointer disabled:opacity-50"
                title="Sync balance from broker"
              >
                <RefreshCw size={13} className={syncMutation.isPending ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={() => setShowSettingsModal(true)}
                className="w-7 h-7 rounded-md flex items-center justify-center text-ink-4 hover:text-ink-2 hover:bg-surface-2 transition-colors cursor-pointer"
                title="Account settings"
              >
                <Settings size={13} />
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-7 h-7 rounded-md flex items-center justify-center text-ink-4 hover:text-red hover:bg-red/10 transition-colors cursor-pointer"
                title="Delete account"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>

          {/* Row 2 — Badges */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge color="blue">{account.currency}</Badge>
            {account.leverage > 1 && (
              <Badge color="default">1:{account.leverage}</Badge>
            )}
            <span className="text-[10px] font-mono text-ink-4">#{account.login_id}</span>
            {bots.length > 0 && (
              <span className="text-[10px] text-ink-4">
                · {bots.length} bot{bots.length !== 1 ? 's' : ''}{runningCount > 0 ? ` · ${runningCount} running` : ''}
              </span>
            )}
          </div>

          {/* Row 3 — Metadata */}
          <div className="flex items-center gap-2 flex-wrap text-[10px] text-ink-4">
            <span>{account.server}</span>
            {account.engine_version && (
              <>
                <span>·</span>
                <span>v{account.engine_version}</span>
              </>
            )}
            {account.last_sync_time && (
              <>
                <span>·</span>
                <span className="flex items-center gap-0.5">
                  <RefreshCw size={8} />
                  {timeAgo(new Date(Number(account.last_sync_time)).toISOString())}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Balance + Allocation */}
        {account.synced_balance != null ? (
          <div className="px-4 pt-3 pb-0">
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div>
                <p className="text-[10px] text-ink-4 mb-0.5">Balance</p>
                <p className="text-sm font-bold font-mono">{formatCurrency(account.synced_balance, account.currency || 'USD')}</p>
              </div>
              <div>
                <p className="text-[10px] text-ink-4 mb-0.5">Equity</p>
                <p className="text-sm font-bold font-mono">
                  {account.synced_equity != null ? formatCurrency(account.synced_equity, account.currency || 'USD') : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-ink-4 mb-0.5">Unallocated</p>
                <p className={`text-sm font-bold font-mono ${unallocated >= 0 ? '' : 'text-red'}`}>
                  {formatCurrency(unallocated, account.currency || 'USD')}
                </p>
              </div>
            </div>

            {/* Allocation Bar */}
            {totalBalance > 0 && bots.length > 0 && (
              <div className="space-y-1.5 mb-1">
                <div className="w-full h-2 rounded-full bg-surface-2 overflow-hidden flex">
                  {bots
                    .filter(b => b.assigned_capital > 0)
                    .map(bot => {
                      const pct = Math.min((bot.assigned_capital / totalBalance) * 100, 100);
                      return (
                        <div
                          key={bot.id}
                          className="h-full bg-orange/50"
                          style={{ width: `${pct}%` }}
                          title={`${bot.name}: ${formatCurrency(bot.assigned_capital, account.currency || 'USD')}`}
                        />
                      );
                    })}
                  {unallocated > 0 && (
                    <div
                      className="h-full bg-green/25"
                      style={{ width: `${(unallocated / totalBalance) * 100}%` }}
                      title={`Unallocated: ${formatCurrency(unallocated, account.currency || 'USD')}`}
                    />
                  )}
                </div>
                {/* Legend */}
                <div className="flex items-center gap-3 text-[9px] text-ink-4">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-orange/50" />
                    Allocated
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-green/25" />
                    Unallocated
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="px-4 pt-3 pb-1">
            {account.created_at && Date.now() - new Date(account.created_at).getTime() > 5 * 60 * 1000 ? (
              <div className="flex items-center gap-2.5 bg-amber/5 border border-amber/15 rounded-lg px-3 py-2.5">
                <RefreshCw size={14} className="text-amber flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[11px] font-semibold text-ink-2">Balance not synced</p>
                  <p className="text-[10px] text-ink-4 mt-0.5">
                    Could not fetch balance from broker. Click sync to retry.
                  </p>
                </div>
                <button
                  onClick={handleSync}
                  disabled={syncMutation.isPending}
                  className="text-[10px] text-amber font-semibold underline hover:text-amber/80 cursor-pointer"
                >
                  {syncMutation.isPending ? 'Syncing...' : 'Sync now'}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2.5 bg-orange/5 border border-orange/15 rounded-lg px-3 py-2.5">
                <Loader2 size={14} className="text-orange animate-spin flex-shrink-0" />
                <div>
                  <p className="text-[11px] font-semibold text-ink-2">Syncing account data...</p>
                  <p className="text-[10px] text-ink-4 mt-0.5">
                    Connecting to broker to fetch balance, leverage, and symbol data. This may take a minute.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Inline Bot List */}
        {bots.length > 0 && (
          <div className="mx-4 mt-3 border-t border-border pt-2 pb-3">
            {bots.map(bot => (
              <Link
                key={bot.id}
                to={`/bots/${bot.id}`}
                className="flex items-center gap-2 py-1.5 px-2 -mx-2 rounded-md hover:bg-surface-2 transition-colors group"
              >
                <Bot size={12} className={`flex-shrink-0 ${bot.is_active ? (bot.botMode === 'PAPER' ? 'text-blue' : 'text-green') : 'text-ink-4'}`} />
                <span className="text-xs font-semibold truncate flex-1 min-w-0 group-hover:text-orange transition-colors">{bot.name}</span>
                <span className={`text-[10px] font-mono flex-shrink-0 ${pnlColor(bot.combined_pnl ?? bot.total_pnl)}`}>
                  {formatPnl(bot.combined_pnl ?? bot.total_pnl, account.currency || 'USD')}
                </span>
                <Badge color={bot.is_active ? (bot.botMode === 'PAPER' ? 'blue' : 'green') : 'default'}>
                  {bot.is_active ? (bot.botMode === 'PAPER' ? 'Paper' : 'Live') : 'Off'}
                </Badge>
              </Link>
            ))}
          </div>
        )}

        {/* Empty bot state */}
        {bots.length === 0 && (
          <div className="mx-4 mt-3 border-t border-border pt-3 pb-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-ink-4">No bots on this account</p>
              <Link to="/bots/new">
                <Button variant="ghost" size="sm">
                  <Plus size={10} />
                  Create Bot
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => !deleteMutation.isPending && setShowDeleteModal(false)}
        title="Delete MT5 Account"
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Account'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-2">
            Are you sure you want to delete <strong>{account.name || account.login_id}</strong>?
          </p>
          <div className="bg-red/5 border border-red/15 rounded-lg p-3">
            <p className="text-xs text-red/80">
              {bots.length > 0
                ? `This will permanently delete this MT5 account and ${bots.length} associated bot${bots.length !== 1 ? 's' : ''}, including all trade history and capital transactions. This action cannot be undone.`
                : 'This will permanently delete this MT5 account. This action cannot be undone.'}
            </p>
          </div>
          {deleteMutation.isError && (
            <div className="bg-red/5 border border-red/15 rounded-lg p-3">
              <p className="text-xs text-red font-semibold">
                {(deleteMutation.error as any)?.response?.data?.message || 'Failed to delete account'}
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Settings Modal */}
      <AccountSettingsModal
        account={account}
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />
    </>
  );
}

function AccountSettingsModal({
  account,
  open,
  onClose,
}: {
  account: Mt5Account;
  open: boolean;
  onClose: () => void;
}) {
  const updateMutation = useUpdateMt5Account();
  const [name, setName] = useState(account.name || '');

  const handleSave = () => {
    updateMutation.mutate(
      { id: account.id, name: name.trim() || undefined },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open={open}
      onClose={() => !updateMutation.isPending && onClose()}
      title="Account Settings"
      footer={
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Display Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={`Account #${account.login_id}`}
        />
        {updateMutation.isError && (
          <div className="bg-red/5 border border-red/15 rounded-lg p-3">
            <p className="text-xs text-red font-semibold">
              {(updateMutation.error as any)?.response?.data?.message || 'Failed to update account'}
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

function EmptyState() {
  return (
    <div className="glass-sm rounded-lg p-10 text-center">
      <Wallet size={40} className="text-orange/40 mx-auto mb-3" />
      <h3 className="text-sm font-bold mb-1">No accounts linked</h3>
      <p className="text-xs text-ink-3 mb-4">
        Add your MT5 trading account to start creating bots.
      </p>
      <Link to="/accounts/new">
        <Button size="sm">
          <Plus size={14} />
          Add MT5 Account
        </Button>
      </Link>
      <div className="mt-5 pt-5 border-t border-border">
        <p className="text-[10px] text-ink-4 mb-1.5">New to algo trading?</p>
        <a
          href="https://partner-tracking.deriv.com/click?a=2204&o=1&c=3&link_id=1"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] font-semibold text-orange hover:text-orange/80 transition-colors"
        >
          Start with Deriv — open a free MT5 account →
        </a>
      </div>
    </div>
  );
}
